import { ClientJS } from "clientjs";
import { SignUpWizardKeys } from "../../../../../routes/signup_wizard_routes";
import GetRelativeBySignupTokenUseCase from "../../../usecases/get_relative_by_signup_token_usecase";
import PutStatusRelativesUsecase from "../../../usecases/put_status_relatives_usecase";
import {
  SET_CONFIRM_EMAIL_STATUS,
  SET_RELATIVE_DATA,
  SET_FIELD_VALUE,
  SET_ACCEPT_TERMS_STATUS,
} from "./SignupWizard.mutations";
import moment from "moment";
import GetIpUsecase from "../../../usecases/get_ip_usecase";
import AcceptNoticeUsecase from "../../../usecases/accept_notice_usecase";

const LOCAL_STORAGE_KEY = "_$WIZARD";

export const SignUpStatus = {
  PENDING_TO_CONFIRM_EMAIL: "Pending to confirm email",
  PENDING_TO_ACCEPT_AGREEMENT: "Pending to accept agreement",
  PENDING_TO_GIVE_GENERAL_INFO: "Pending to give general info",
  PENDING_TO_GIVE_INVOICE_INFO: "Pending to give invoice info",
  PENDING_TO_GIVE_SHIPMENT_INFO: "Pending to give shipment info",
  PENDING_TO_ASSOCIATE_CHILDREN: "Pending to associate children",
  CHILDREN_READY: "Children ready",
};

export const SignUpRouteByStatus = (status) => {
  switch (status) {
    case SignUpStatus.PENDING_TO_ACCEPT_AGREEMENT:
    case SignUpStatus.PENDING_TO_GIVE_GENERAL_INFO:
      return SignUpWizardKeys.generalInfo;
    case SignUpStatus.PENDING_TO_GIVE_INVOICE_INFO:
      return SignUpWizardKeys.invoiceInfo;
    case SignUpStatus.PENDING_TO_GIVE_SHIPMENT_INFO:
      return SignUpWizardKeys.shippingInfo;
    case SignUpStatus.PENDING_TO_ASSOCIATE_CHILDREN:
      return SignUpWizardKeys.childrenInfo;
    default:
      return "/dashboard";
  }
};

function initialState() {
  return {
    confirmEmailStatus: null,
    acceptTermsStatus: {},
    relative: null,

    // Privacy notice modal
    acceptTerms: false,
  };
}

const SignupWizardStore = {
  namespaced: true,

  state: localStorage.getItem(LOCAL_STORAGE_KEY)
    ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY))
    : initialState,

  mutations: {
    [SET_CONFIRM_EMAIL_STATUS](state, status) {
      state.confirmEmailStatus = status;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    },
    [SET_RELATIVE_DATA](state, relative) {
      state.relative = relative;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    },
    [SET_FIELD_VALUE](state, { field, value }) {
      state[field] = value;
    },
    [SET_ACCEPT_TERMS_STATUS](state, status) {
      state.acceptTermsStatus = status;
    },
  },

  getters: {
    saveNoticePrivacyButtonEnabled(state) {
      return state.acceptTerms === true;
    },
  },

  actions: {
    async getRelativeByToken({ commit }, signUpToken) {
      const response = await GetRelativeBySignupTokenUseCase(signUpToken);

      response.fold(
        () =>
          commit(SET_CONFIRM_EMAIL_STATUS, {
            status: "error",
            message: "Error fetching relative by token",
          }),

        async (relative) => {
          const { signUpStatus } = relative;

          commit(SET_RELATIVE_DATA, relative);

          if (signUpStatus == SignUpStatus.PENDING_TO_CONFIRM_EMAIL) {
            const request = {
              ...relative,
              signUpStatus: SignUpStatus.PENDING_TO_ACCEPT_AGREEMENT,
            };
            const updateOrFailure = await PutStatusRelativesUsecase(request);

            updateOrFailure.fold(
              () =>
                commit(SET_CONFIRM_EMAIL_STATUS, {
                  status: "error",
                  message: "Error updating relative status",
                }),
              () => {
                commit(SET_CONFIRM_EMAIL_STATUS, {
                  status: "success",
                  message: "Email confirmed",
                  signUpStatus: SignUpStatus.PENDING_TO_ACCEPT_AGREEMENT,
                });
                commit(SET_RELATIVE_DATA, request);
              }
            );
          } else {
            commit(SET_CONFIRM_EMAIL_STATUS, {
              status: "success",
              message: "Already with email confirmed",
              signUpStatus,
            });
          }
        }
      );
    },

    updateField({ commit }, { field, value }) {
      commit(SET_FIELD_VALUE, { field, value });
    },

    async saveNoticePrivacy({ commit, state }) {
      commit(SET_ACCEPT_TERMS_STATUS, { status: "loading" });
      const client = new ClientJS();
      const ipOrFailure = await GetIpUsecase();

      if (ipOrFailure.isLeft) {
        return commit(SET_ACCEPT_TERMS_STATUS, {
          status: "error",
          message: "Error fetching ip",
        });
      }

      // Verificar si la API Permissions está disponible
      if (!navigator.permissions) {
        return commit(SET_ACCEPT_TERMS_STATUS, {
          status: "error",
          message: "La API Permissions no está soportada en este navegador.",
        });
      }

      const request = {
        browser: client.getBrowser(),
        browserVersion: client.getBrowserVersion(),
        engine: client.getEngine(),
        engineVersion: client.getEngineVersion(),
        language: navigator.language,
        os: client.getOS(),
        osVersion: client.getOSVersion(),
        screenPrint: `Current Resolution: ${screen.width}x${screen.height}, Available Resolution: ${screen.availWidth}x${screen.availHeight}, Color Depth: ${screen.colorDepth}, Device XDPI: ${screen.deviceXDPI}, Device YDPI: ${screen.deviceYDPI}`,
        screenResolution: `${screen.availWidth}x${screen.availHeight}`,
        timeZone: String(String(new Date()).split("(")[1]).split(")")[0],
        userAgent: client.getUserAgent(),
        ipAddress: ipOrFailure.get().data.ip,
        checkedTime: moment().format("YYYY-MM-DD"),
        fullUrl: window.location.href,
        idRelativeInt: state.relative.idRelativeInt,
      };

      function getPosition() {
        return new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
      }

      let permitedPosition = {
        coords: {
          latitude: null,
          longitude: null,
          positionAccuracy: null,
        },
      };

      navigator.permissions
        .query({ name: "geolocation" })
        .then((permissionStatus) => {
          if (
            permissionStatus.state === "prompt" ||
            permissionStatus.state === "granted"
          ) {
            getPosition()
              .then((position) => {
                permitedPosition = position;

                commit(SET_ACCEPT_TERMS_STATUS, {
                  status: "success",
                  message: "Ubicación obtenida correctamente",
                  position: permitedPosition,
                });
              })
              .catch(() => {
                console.log(
                  "El usuario ha denegado los permisos de geolocalización"
                );
              });
          } else if (permissionStatus.state === "denied") {
            console.log(
              "El usuario ha denegado los permisos de geolocalización"
            );
          }
        })
        .catch((error) => {
          console.log("Error al obtener permisos: " + error);
        });

      request.latitude = permitedPosition.coords.latitude;
      request.longitude = permitedPosition.coords.longitude;
      request.positionAccuracy = permitedPosition.coords.accuracy;

      const acceptOrFailure = await AcceptNoticeUsecase(request);

      acceptOrFailure.fold(
        () =>
          commit(SET_ACCEPT_TERMS_STATUS, {
            status: "error",
            message: "Error accepting notice",
          }),
        async () => {
          //! Once the notice privacy is accepted, we need to update the status of the relative
          const relativeRequest = {
            ...state.relative,
            signUpStatus: SignUpStatus.PENDING_TO_GIVE_GENERAL_INFO,
          };
          const updateStatusOrFailure = await PutStatusRelativesUsecase(
            relativeRequest
          );

          updateStatusOrFailure.fold(
            () =>
              commit(SET_ACCEPT_TERMS_STATUS, {
                status: "error",
                message: "Error updating status",
              }),
            () => {
              commit(SET_ACCEPT_TERMS_STATUS, {
                status: "success",
                message: "Notice accepted",
              });
              commit(SET_RELATIVE_DATA, relativeRequest);
            }
          );
        }
      );
    },
  },
};

export default SignupWizardStore;
